import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

/**
 * Grafieken voor de body-composition-pagina (PAX-6969): gewicht en som van 8
 * over tijd (met doelzoneband), combinatiegrafiek met twee y-assen en
 * spreidingsdiagrammen met trendlijn uit de server-side LineaireRegressie.
 *
 * Patroon cf. zweettest-charts.js: Alpine-component met dark-mode observer;
 * de tijdsas is een echte (lineaire) as op milliseconden, zodat ongelijk
 * gespreide meetsessies ook ongelijk gespreid staan.
 */

function themeColors() {
  const dark = document.documentElement.classList.contains('dark');
  return {
    dark,
    text: dark ? '#9ca3af' : '#6b7280',
    grid: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
    point: dark ? '#60a5fa' : '#2563eb',
    pointZacht: dark ? 'rgba(96,165,250,0.35)' : 'rgba(37,99,235,0.30)',
    line: dark ? '#a78bfa' : '#7c3aed',
    lijn2: dark ? '#34d399' : '#059669',
    fill: dark ? 'rgba(167,139,250,0.10)' : 'rgba(124,58,237,0.08)',
    band: dark ? 'rgba(52,211,153,0.10)' : 'rgba(16,185,129,0.08)',
    trend: dark ? '#f59e0b' : '#d97706',
    piek: dark ? 'rgba(250,204,21,0.16)' : 'rgba(234,179,8,0.14)',
    piekRand: dark ? 'rgba(250,204,21,0.55)' : 'rgba(202,138,4,0.55)',
    piekTekst: dark ? '#fcd34d' : '#a16207',
    tooltipBg: dark ? '#1f2937' : '#111827',
  };
}

function baseComponent(render) {
  return {
    _chart: null,
    _observer: null,
    // PAX-7732 — vlaggen van de toggles "Toon piekperiodes"/"Toon jaarplanning";
    // de view duwt ze via x-effect="setOpties(...)" uit de omliggende scope.
    _opties: { pieken: true, jaarplanning: true },

    init() {
      this.$nextTick(() => this.render());
      this._observer = new MutationObserver(() => this.render());
      this._observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class'],
      });
    },

    destroy() {
      this._observer?.disconnect();
      this._chart?.destroy();
    },

    /**
     * Zet de weergave-opties en hertekent. Vóór de eerste render bestaat er nog
     * geen chart — die init-render (in $nextTick) pikt de vlaggen zelf op, dus
     * hertekenen we enkel bij een echte wijziging nadien.
     */
    setOpties(pieken = true, jaarplanning = true) {
      this._opties = { pieken: pieken !== false, jaarplanning: jaarplanning !== false };
      if (this._chart) this.render();
    },

    render,
  };
}

function datumKort(iso) {
  const dt = new Date(String(iso).replace(' ', 'T'));
  return Number.isNaN(dt.getTime())
    ? String(iso)
    : dt.toLocaleDateString('nl-BE', { day: 'numeric', month: 'short', year: 'numeric' });
}

const ms = (iso) => new Date(String(iso) + 'T00:00:00').getTime();

/** Echte tijdsas als lineaire as op ms, met korte datumticks. */
function tijdAs(kleuren) {
  return {
    type: 'linear',
    ticks: {
      color: kleuren.text,
      maxTicksLimit: 8,
      callback: (v) => new Date(v).toLocaleDateString('nl-BE', { day: 'numeric', month: 'short', year: '2-digit' }),
    },
    grid: { display: false },
  };
}

/** Horizontale doelzoneband op de (eerste) y-as. */
function doelzoneBand(zone, kleur, asId = 'y') {
  return {
    id: 'doelzoneband',
    beforeDatasetsDraw(chart) {
      const { ctx, chartArea, scales } = chart;
      const as = scales[asId];
      if (!chartArea || !as || !zone || (zone.min == null && zone.max == null)) return;
      const klem = (v) => Math.min(Math.max(as.getPixelForValue(v), chartArea.top), chartArea.bottom);
      const boven = zone.max != null ? klem(zone.max) : chartArea.top;
      const onder = zone.min != null ? klem(zone.min) : chartArea.bottom;
      if (onder <= boven) return;
      ctx.save();
      ctx.fillStyle = kleur;
      ctx.fillRect(chartArea.left, boven, chartArea.right - chartArea.left, onder - boven);
      ctx.restore();
    },
  };
}

/**
 * Bandkleur per jaarplanning-periodetype (PAX-7083): rgba-equivalenten van de
 * $typeKleur-map op het jaaroverzicht (slate/sky/indigo/orange/rose-400),
 * met iets meer dekking in dark mode zodat de band leesbaar blijft.
 */
function periodeTypeKleur(type, dark) {
  const kleuren = {
    off_season: dark ? 'rgba(148,163,184,0.22)' : 'rgba(148,163,184,0.18)',
    general_prep: dark ? 'rgba(56,189,248,0.18)' : 'rgba(56,189,248,0.15)',
    specific_prep: dark ? 'rgba(129,140,248,0.20)' : 'rgba(129,140,248,0.16)',
    peak: dark ? 'rgba(251,146,60,0.20)' : 'rgba(251,146,60,0.16)',
    final_races: dark ? 'rgba(251,113,133,0.20)' : 'rgba(251,113,133,0.16)',
  };
  return kleuren[type];
}

/**
 * Doelzonebanden per datumbereik (PAX-7047, jaarplanning-periodes): zelfde
 * band als doelzoneBand maar horizontaal geknipt op [start, eind] per periode.
 * Eind is inclusief, dus de band loopt tot het einde van die dag.
 *
 * PAX-7083: kleur per periode-type + hover-tooltip met label, doelrange en
 * strategie; de punt-tooltip van Chart.js heeft voorrang op de band-tooltip.
 *
 * PAX-7732: meerdere assen per periode via `assen` — de combinatiegrafiek
 * tekent de gewichtsband op de linker-as en de som8-band op de rechter-as, met
 * één tooltip die beide ranges opsomt.
 */
export function periodeBanden(periodes, t, opties = {}) {
  const DAG = 86400000;
  const assen = opties.assen ?? [{
    asId: opties.asId ?? 'y',
    minVeld: 'min',
    maxVeld: 'max',
    eenheid: opties.eenheid ?? '',
    decimalen: opties.decimalen ?? 1,
    naam: null,
  }];
  let rects = [];
  let hover = null;

  const rangeTekst = (p, as) => {
    const dec = as.decimalen ?? 1;
    const f = (v) => Number(v).toFixed(dec);
    const min = p[as.minVeld];
    const max = p[as.maxVeld];
    const kern = min != null && max != null
      ? `${f(min)}–${f(max)} ${as.eenheid}`
      : `${min != null ? `≥ ${f(min)}` : `≤ ${f(max)}`} ${as.eenheid}`;
    return as.naam ? `${as.naam}: ${kern}` : kern;
  };

  const regelsVoor = (p) => {
    const regels = [p.label];
    for (const as of assen) {
      if (p[as.minVeld] != null || p[as.maxVeld] != null) regels.push(rangeTekst(p, as));
    }
    if (p.strategie) regels.push(p.strategie);
    return regels;
  };

  return {
    id: 'periodebanden',
    beforeDatasetsDraw(chart) {
      const { ctx, chartArea, scales } = chart;
      const xAs = scales.x;
      rects = [];
      if (!chartArea || !xAs || !periodes?.length) return;
      const klemX = (v) => Math.min(Math.max(xAs.getPixelForValue(v), chartArea.left), chartArea.right);
      ctx.save();
      for (const p of periodes) {
        const links = klemX(ms(p.start));
        const rechts = klemX(ms(p.eind) + DAG);
        if (rechts <= links) continue;
        for (const asSpec of assen) {
          const as = scales[asSpec.asId];
          const min = p[asSpec.minVeld];
          const max = p[asSpec.maxVeld];
          if (!as || (min == null && max == null)) continue;
          const klemY = (v) => Math.min(Math.max(as.getPixelForValue(v), chartArea.top), chartArea.bottom);
          const boven = max != null ? klemY(max) : chartArea.top;
          const onder = min != null ? klemY(min) : chartArea.bottom;
          if (onder <= boven) continue;
          ctx.fillStyle = periodeTypeKleur(p.type, t.dark) ?? t.band;
          ctx.fillRect(links, boven, rechts - links, onder - boven);
          rects.push({ p, x: links, y: boven, w: rechts - links, h: onder - boven });
        }
      }
      ctx.restore();
    },
    afterEvent(chart, args) {
      const e = args.event;
      if (e.type === 'mouseout') {
        if (hover) {
          hover = null;
          args.changed = true;
        }
        return;
      }
      if (e.type !== 'mousemove') return;
      const r = rects.find((b) => e.x >= b.x && e.x <= b.x + b.w && e.y >= b.y && e.y <= b.y + b.h) ?? null;
      if (r?.p !== hover?.p || (r && hover && (hover.ex !== e.x || hover.ey !== e.y))) {
        hover = r ? { p: r.p, ex: e.x, ey: e.y } : null;
        args.changed = true;
      }
    },
    afterDraw(chart) {
      // Punt-tooltip en piek-tooltip (PAX-7084) winnen van de band-tooltip.
      if (!hover || chart.tooltip?.getActiveElements()?.length || chart.$piekHover) return;
      canvasTooltip(chart, t, regelsVoor(hover.p), hover.ex, hover.ey);
    },
  };
}

/** Eigen canvas-tooltip (eerste regel vet), gepositioneerd binnen de chartArea. */
function canvasTooltip(chart, t, regels, ex, ey) {
  const { ctx, chartArea } = chart;
  ctx.save();
  ctx.font = '11px system-ui, sans-serif';
  const pad = 8;
  const lijnH = 15;
  const breedte = Math.max(...regels.map((r) => ctx.measureText(String(r)).width)) + pad * 2;
  const hoogte = regels.length * lijnH + pad * 2 - 4;
  const x = Math.max(chartArea.left, Math.min(ex + 12, chartArea.right - breedte));
  const y = Math.max(chartArea.top, Math.min(ey - hoogte - 8, chartArea.bottom - hoogte));
  ctx.fillStyle = t.tooltipBg;
  ctx.beginPath();
  ctx.roundRect(x, y, breedte, hoogte, 6);
  ctx.fill();
  ctx.fillStyle = '#f9fafb';
  regels.forEach((regel, i) => {
    ctx.font = i === 0 ? 'bold 11px system-ui, sans-serif' : '11px system-ui, sans-serif';
    ctx.fillText(String(regel), x + pad, y + pad + 10 + i * lijnH);
  });
  ctx.restore();
}

/**
 * Piek-markers (PAX-7084, jaarplanning-piekkoersen): verticale stripe over de
 * volledige charthoogte van datum_van t.e.m. datum_tot (einddatum inclusief,
 * dus +1 dag), met naam-label en hover-tooltip. Eendaagse pieken krijgen een
 * minimale breedte zodat de marker zichtbaar blijft; het label staat
 * horizontaal als het past en anders 90° gedraaid, met truncatie.
 *
 * De piek-tooltip wint van de band-tooltip (via chart.$piekHover); de
 * punt-tooltip van Chart.js wint van allebei.
 */
export function piekMarkers(pieken, t, labels) {
  const DAG = 86400000;
  let rects = [];
  let hover = null;

  const trunc = (ctx, tekst, maxBreedte) => {
    if (ctx.measureText(tekst).width <= maxBreedte) return tekst;
    let kort = tekst;
    while (kort.length > 1 && ctx.measureText(`${kort}…`).width > maxBreedte) {
      kort = kort.slice(0, -1);
    }
    return `${kort}…`;
  };

  const datumTekst = (p) => (p.van === p.tot ? datumKort(p.van) : `${datumKort(p.van)} – ${datumKort(p.tot)}`);

  return {
    id: 'piekmarkers',
    beforeDatasetsDraw(chart) {
      const { ctx, chartArea, scales } = chart;
      const xAs = scales.x;
      rects = [];
      if (!chartArea || !xAs || !pieken?.length) return;
      ctx.save();
      ctx.beginPath();
      ctx.rect(chartArea.left, chartArea.top, chartArea.right - chartArea.left, chartArea.bottom - chartArea.top);
      ctx.clip();
      for (const p of pieken) {
        let links = xAs.getPixelForValue(ms(p.van));
        let rechts = xAs.getPixelForValue(ms(p.tot) + DAG);
        if (rechts < chartArea.left || links > chartArea.right) continue;
        if (rechts - links < 4) {
          // Eendaagse piek: minimaal 4px breed rond het middelpunt.
          const mid = (links + rechts) / 2;
          links = mid - 2;
          rechts = mid + 2;
        }
        const breedte = rechts - links;
        const hoogte = chartArea.bottom - chartArea.top;
        ctx.fillStyle = t.piek;
        ctx.fillRect(links, chartArea.top, breedte, hoogte);
        ctx.strokeStyle = t.piekRand;
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 3]);
        ctx.beginPath();
        ctx.moveTo(links + 0.5, chartArea.top);
        ctx.lineTo(links + 0.5, chartArea.bottom);
        ctx.moveTo(rechts - 0.5, chartArea.top);
        ctx.lineTo(rechts - 0.5, chartArea.bottom);
        ctx.stroke();
        ctx.setLineDash([]);

        ctx.fillStyle = t.piekTekst;
        ctx.font = 'bold 10px system-ui, sans-serif';
        const naam = String(p.naam);
        if (ctx.measureText(naam).width + 8 <= breedte) {
          ctx.fillText(naam, links + 4, chartArea.top + 11);
        } else {
          // Smalle stripe: label 90° gedraaid langs de linkerrand, getrunceerd.
          ctx.save();
          ctx.translate(links + Math.min(breedte / 2 + 4, 10), chartArea.top + 4);
          ctx.rotate(Math.PI / 2);
          ctx.fillText(trunc(ctx, naam, hoogte - 8), 0, 0);
          ctx.restore();
        }
        rects.push({ p, x: Math.max(links, chartArea.left), rechts: Math.min(rechts, chartArea.right) });
      }
      ctx.restore();
    },
    afterEvent(chart, args) {
      const e = args.event;
      if (e.type === 'mouseout') {
        if (hover) {
          hover = null;
          chart.$piekHover = false;
          args.changed = true;
        }
        return;
      }
      if (e.type !== 'mousemove') return;
      const { chartArea } = chart;
      const inArea = chartArea && e.y >= chartArea.top && e.y <= chartArea.bottom;
      const r = inArea ? rects.find((b) => e.x >= b.x && e.x <= b.rechts) ?? null : null;
      if (r?.p !== hover?.p || (r && hover && (hover.ex !== e.x || hover.ey !== e.y))) {
        hover = r ? { p: r.p, ex: e.x, ey: e.y } : null;
        args.changed = true;
      }
      chart.$piekHover = hover !== null;
    },
    afterDraw(chart) {
      if (!hover || chart.tooltip?.getActiveElements()?.length) return;
      const regels = [hover.p.naam, datumTekst(hover.p)];
      if (labels?.piek) regels.push(labels.piek);
      canvasTooltip(chart, t, regels, hover.ex, hover.ey);
    },
  };
}

/**
 * Weergave-toggles boven de grafieken (PAX-7732): "Toon piekperiodes" en
 * "Toon jaarplanning". De keuze wordt per browser bewaard, zodat een coach ze
 * niet op elke rennerfiche opnieuw moet instellen; de grafieken lezen de
 * vlaggen via x-effect="setOpties(toonPieken, toonJaarplanning)".
 */
const OPTIES_SLEUTEL = 'pax.bodycomp.grafiek.opties';

export function bodycompGrafiekOpties() {
  return {
    toonPieken: true,
    toonJaarplanning: true,

    init() {
      try {
        const bewaard = JSON.parse(localStorage.getItem(OPTIES_SLEUTEL) ?? '{}');
        if (typeof bewaard.pieken === 'boolean') this.toonPieken = bewaard.pieken;
        if (typeof bewaard.jaarplanning === 'boolean') this.toonJaarplanning = bewaard.jaarplanning;
      } catch {
        // Geen of onleesbare voorkeur → standaard staat alles aan.
      }
      this.$watch('toonPieken', () => this.bewaarOpties());
      this.$watch('toonJaarplanning', () => this.bewaarOpties());
    },

    bewaarOpties() {
      try {
        localStorage.setItem(OPTIES_SLEUTEL, JSON.stringify({
          pieken: this.toonPieken,
          jaarplanning: this.toonJaarplanning,
        }));
      } catch {
        // Private mode e.d.: niet kunnen bewaren mag de grafieken niet breken.
      }
    },
  };
}

function tooltipTitelDatumLabel(items) {
  const raw = items[0].raw;
  const titel = datumKort(raw.datum);
  return raw.label ? `${titel} — ${raw.label}` : titel;
}

/**
 * Lijngrafiek van één waarde over tijd met optionele doelzoneband en
 * optionele ongelabelde dagpunten (bv. renner_tijdlijn type=gewicht).
 *
 * cfg: {
 *   sessies: [{ x: 'Y-m-d', y, label }],
 *   sessiesDexa?: [{ x: 'Y-m-d', y, label }] — optionele DEXA-reeks (PAX-7765),
 *   dagpunten?: [{ datum, waarde }],
 *   zone?: { min, max } | null,
 *   periodes?: [{ start, eind, min, max }] — jaarplanning-banden; vervangen de vlakke zone,
 *   pieken?: [{ naam, van, tot }] — piekkoersen als verticale markers,
 *   eenheid: 'kg' | 'mm' | '%',
 *   decimalen: 1 | 2,
 *   labels: { sessies, sessiesDexa?, dagwaarden },
 * }
 */
export function bodycompTijdChart(cfg) {
  return baseComponent(function () {
    this._chart?.destroy();
    this._chart = null;

    const canvas = this.$refs.chart;
    const heeftData = cfg.sessies.length || cfg.sessiesDexa?.length;
    if (!canvas || !heeftData) return;

    const t = themeColors();
    const dec = cfg.decimalen ?? 1;

    const datasets = [];

    if (cfg.sessies.length) {
      datasets.push({
        label: cfg.labels.sessies,
        data: cfg.sessies.map((p) => ({ x: ms(p.x), y: p.y, datum: p.x, label: p.label })),
        borderColor: t.line,
        backgroundColor: t.fill,
        pointBackgroundColor: t.point,
        pointRadius: 4,
        pointHoverRadius: 6,
        borderWidth: 2,
        tension: 0.25,
      });
    }

    if (cfg.sessiesDexa?.length) {
      datasets.push({
        label: cfg.labels.sessiesDexa ?? 'DEXA',
        data: cfg.sessiesDexa.map((p) => ({ x: ms(p.x), y: p.y, datum: p.x, label: p.label })),
        borderColor: t.lijn2,
        backgroundColor: 'transparent',
        pointBackgroundColor: t.lijn2,
        pointRadius: 5,
        pointHoverRadius: 7,
        pointStyle: 'rectRot',
        borderWidth: 2,
        tension: 0.25,
      });
    }

    if (cfg.dagpunten?.length) {
      datasets.push({
        label: cfg.labels.dagwaarden,
        data: cfg.dagpunten.map((p) => ({ x: ms(p.datum), y: p.waarde, datum: p.datum, label: null })),
        showLine: false,
        pointBackgroundColor: t.pointZacht,
        borderColor: t.pointZacht,
        pointRadius: 2.5,
        pointHoverRadius: 4,
      });
    }

    this._chart = new Chart(canvas, {
      type: 'line',
      data: { datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: datasets.length > 1, labels: { color: t.text, boxWidth: 12, usePointStyle: true } },
          tooltip: {
            backgroundColor: t.tooltipBg,
            padding: 10,
            cornerRadius: 6,
            callbacks: {
              title: tooltipTitelDatumLabel,
              label: (ctx) => `${ctx.parsed.y.toFixed(dec)} ${cfg.eenheid}`,
            },
          },
        },
        scales: {
          x: tijdAs(t),
          y: {
            title: { display: true, text: cfg.eenheid, color: t.text },
            ticks: { color: t.text },
            grid: { color: t.grid },
          },
        },
      },
      plugins: [
        // PAX-7732 — met "Toon jaarplanning" uit valt de grafiek terug op de
        // vlakke doelzone uit het profiel.
        this._opties.jaarplanning && cfg.periodes?.length
          ? periodeBanden(cfg.periodes, t, { eenheid: cfg.eenheid, decimalen: dec })
          : doelzoneBand(cfg.zone, t.band),
        // Piek-stripes ná de banden, zodat ze er bovenop getekend worden.
        ...(this._opties.pieken && cfg.pieken?.length ? [piekMarkers(cfg.pieken, t, cfg.labels)] : []),
      ],
    });
  });
}

/**
 * Combinatiegrafiek gewicht (linker-as) + som van 8 (rechter-as) over tijd.
 *
 * cfg: {
 *   punten: [{ x, gewicht, som8, label }],
 *   periodes?: [{ start, eind, gewicht_min, gewicht_max, som8_min, som8_max, type, label, strategie }]
 *     — jaarplanning-banden (PAX-7732): gewicht op de linker-as, som8 op de rechter-as,
 *   pieken?: [{ naam, van, tot }] — piekkoersen als verticale markers,
 *   labels: { gewicht, som8, piek },
 * }
 */
export function bodycompComboChart(cfg) {
  return baseComponent(function () {
    this._chart?.destroy();
    this._chart = null;

    const canvas = this.$refs.chart;
    if (!canvas || !cfg.punten.length) return;

    const t = themeColors();
    const serie = (veld) => cfg.punten
      .filter((p) => p[veld] != null)
      .map((p) => ({ x: ms(p.x), y: p[veld], datum: p.x, label: p.label }));

    this._chart = new Chart(canvas, {
      type: 'line',
      data: {
        datasets: [
          {
            label: cfg.labels.gewicht,
            data: serie('gewicht'),
            borderColor: t.line,
            pointBackgroundColor: t.line,
            yAxisID: 'y',
            pointRadius: 3,
            pointHoverRadius: 5,
            borderWidth: 2,
            tension: 0.25,
          },
          {
            label: cfg.labels.som8,
            data: serie('som8'),
            borderColor: t.lijn2,
            pointBackgroundColor: t.lijn2,
            yAxisID: 'y1',
            pointRadius: 3,
            pointHoverRadius: 5,
            borderWidth: 2,
            tension: 0.25,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: true, labels: { color: t.text, boxWidth: 12 } },
          tooltip: {
            backgroundColor: t.tooltipBg,
            padding: 10,
            cornerRadius: 6,
            callbacks: {
              title: tooltipTitelDatumLabel,
              label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(1)} ${ctx.dataset.yAxisID === 'y' ? 'kg' : 'mm'}`,
            },
          },
        },
        scales: {
          x: tijdAs(t),
          y: {
            position: 'left',
            title: { display: true, text: `${cfg.labels.gewicht} (kg)`, color: t.text },
            ticks: { color: t.text },
            grid: { color: t.grid },
          },
          y1: {
            position: 'right',
            title: { display: true, text: `${cfg.labels.som8} (mm)`, color: t.text },
            ticks: { color: t.text },
            grid: { drawOnChartArea: false },
          },
        },
      },
      plugins: [
        ...(this._opties.jaarplanning && cfg.periodes?.length
          ? [periodeBanden(cfg.periodes, t, {
              assen: [
                { asId: 'y', minVeld: 'gewicht_min', maxVeld: 'gewicht_max', eenheid: 'kg', decimalen: 1, naam: cfg.labels.gewicht },
                { asId: 'y1', minVeld: 'som8_min', maxVeld: 'som8_max', eenheid: 'mm', decimalen: 1, naam: cfg.labels.som8 },
              ],
            })]
          : []),
        ...(this._opties.pieken && cfg.pieken?.length ? [piekMarkers(cfg.pieken, t, cfg.labels)] : []),
      ],
    });
  });
}

/**
 * Spreidingsdiagram van een waarde tegen het gewicht met trendlijn uit de
 * server-side regressie (LineaireRegressie); R² en n staan in de view.
 *
 * cfg: {
 *   punten: [{ x: gewicht, y, datum, label }],
 *   fit: { slope, intercept } | null,
 *   decimalen: 1 | 2,
 *   labels: { x, y },
 * }
 */
export function bodycompScatterChart(cfg) {
  return baseComponent(function () {
    this._chart?.destroy();
    this._chart = null;

    const canvas = this.$refs.chart;
    if (!canvas || !cfg.punten.length) return;

    const t = themeColors();
    const dec = cfg.decimalen ?? 1;

    const gewichten = cfg.punten.map((p) => p.x);
    const xMin = Math.min(...gewichten) - 0.5;
    const xMax = Math.max(...gewichten) + 0.5;

    const datasets = [
      {
        type: 'scatter',
        data: cfg.punten.map((p) => ({ x: p.x, y: p.y, datum: p.datum, label: p.label })),
        backgroundColor: t.point,
        pointRadius: 5,
        pointHoverRadius: 7,
      },
    ];

    if (cfg.fit) {
      datasets.push({
        type: 'line',
        data: [
          { x: xMin, y: cfg.fit.slope * xMin + cfg.fit.intercept },
          { x: xMax, y: cfg.fit.slope * xMax + cfg.fit.intercept },
        ],
        borderColor: t.trend,
        borderWidth: 2,
        borderDash: [6, 4],
        pointRadius: 0,
      });
    }

    this._chart = new Chart(canvas, {
      data: { datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: t.tooltipBg,
            padding: 10,
            cornerRadius: 6,
            filter: (item) => item.datasetIndex === 0,
            callbacks: {
              title: tooltipTitelDatumLabel,
              label: (ctx) => [
                `${cfg.labels.y}: ${ctx.parsed.y.toFixed(dec)}`,
                `${cfg.labels.x}: ${ctx.parsed.x.toFixed(2)} kg`,
              ],
            },
          },
        },
        scales: {
          x: {
            min: xMin,
            max: xMax,
            title: { display: true, text: `${cfg.labels.x} (kg)`, color: t.text },
            ticks: { color: t.text },
            grid: { display: false },
          },
          y: {
            title: { display: true, text: cfg.labels.y, color: t.text },
            ticks: { color: t.text },
            grid: { color: t.grid },
          },
        },
      },
    });
  });
}
